import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trophy, TrendingUp, Package, ShoppingBag, Flag, ClipboardList, BookOpen, AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { toast } from "sonner";
import { API, inr, imgUrl, ROLES } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { CardReveal } from "@/components/CardReveal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GlossyBadge } from "@/components/GlossyBadge";
import { EmptyState } from "@/components/EmptyState";

const ROLE_BADGE = {
  admin: "bg-indigo-100 text-indigo-700 hover:bg-indigo-100",
  accountant: "bg-violet-100 text-violet-700 hover:bg-violet-100",
  sales: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  stock_team: "bg-teal-100 text-teal-700 hover:bg-teal-100",
  stock_head: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  staff: "bg-slate-100 text-slate-600 hover:bg-slate-100",
};

const CHANNEL_COLORS = ["#4F46E5", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0891B2"];
const FIRM2 = ["admin", "staff", "sales", "accountant"];
const EMPTY_FORM = { employee_id: "", color: "yellow", reason: "", task_id: "", sop_id: "" };
const CARD_HEAD = "px-5 pb-2 pt-4";
const CARD_BODY = "px-5 pb-4";

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—";
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" }) : "—");
const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—");
const fmtD = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" });

const REMARK_STYLE = { Late: "text-rose-600", HalfDay: "text-amber-600", LeftEarly: "text-orange-600" };

function RemarkSpan({ e, testid }) {
  const remark = e.remark || (e.late ? "Late" : "");
  if (!remark) return <span className="font-normal text-slate-300">—</span>;
  return (
    <span className="text-xs font-semibold" data-testid={testid}>
      {remark.split("-").map((p, i) => (
        <span key={p}>
          {i > 0 && <span className="text-slate-300">-</span>}
          <span className={REMARK_STYLE[p.trim()] || "text-slate-600"}>{p}</span>
        </span>
      ))}
    </span>
  );
}

function CardTile({ c, showEmployee, onOpen }) {
  const red = c.color === "red";
  const green = c.color === "green";
  const tone = red
    ? "border-rose-200 bg-rose-50/50 hover:bg-rose-50"
    : green
      ? "border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50"
      : "border-amber-200 bg-amber-50/50 hover:bg-amber-50";
  const bar = red ? "bg-red-600" : green ? "bg-emerald-500" : "bg-yellow-400";
  const label = red ? "text-rose-700" : green ? "text-emerald-700" : "text-amber-700";
  return (
    <button
      type="button"
      onClick={() => onOpen(c)}
      data-testid={`card-item-${c.id}`}
      className={`rounded-lg border p-3 text-left transition-shadow hover:shadow-md ${tone}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`h-4 w-2.5 shrink-0 rounded-[2px] shadow-sm ${bar}`} />
        <span className={`text-[11px] font-bold uppercase tracking-wide ${label}`}>{c.color}</span>
        {showEmployee && <span className="truncate text-xs font-semibold text-slate-900">{c.employee_name}</span>}
        <span className="ml-auto shrink-0 text-[10px] text-slate-400">
          {c.issued_by_name} · {fmtShort(c.created_at)}
        </span>
      </div>
      <p className="mt-1 truncate text-xs text-slate-700">{c.reason}</p>
      <div className="mt-1.5 flex items-center gap-1">
        {c.task_title && (
          <span title={`Task: ${c.task_title}`} className="rounded bg-slate-100 p-1 text-slate-500">
            <ClipboardList className="h-3 w-3" />
          </span>
        )}
        {c.sop_title && (
          <span title={`SOP: ${c.sop_title}`} className="rounded bg-slate-100 p-1 text-slate-500">
            <BookOpen className="h-3 w-3" />
          </span>
        )}
        {c.photo_url && (
          <img src={imgUrl(c.photo_url)} alt="Evidence" className="ml-auto h-10 w-10 rounded border border-slate-200 object-cover" data-testid={`card-photo-${c.id}`} />
        )}
      </div>
    </button>
  );
}

function CardDetailDialog({ card, onClose }) {
  const red = card?.color === "red";
  const green = card?.color === "green";
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="card-detail-dialog">
        {card && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <span className={`h-5 w-3.5 rounded-[2px] shadow-sm ${red ? "bg-red-600" : green ? "bg-emerald-500" : "bg-yellow-400"}`} />
                <span className={red ? "text-rose-700" : green ? "text-emerald-700" : "text-amber-700"}>{card.color === "red" ? "Red" : card.color === "green" ? "Green" : "Yellow"} Card</span>
                <span className="text-slate-900">— {card.employee_name}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {card.escalated && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700" data-testid="card-escalated-note">
                  Auto-escalated from Yellow — 3rd yellow card within 90 days.
                </div>
              )}
              <div className="text-xs text-slate-500">
                Issued by <span className="font-medium text-slate-700">{card.issued_by_name}</span> · {fmtDT(card.created_at)} IST
              </div>
              <p className="text-sm leading-relaxed text-slate-800" data-testid="card-detail-reason">{card.reason}</p>
              {(card.task_title || card.sop_title) && (
                <div className="flex flex-wrap gap-1.5">
                  {card.task_title && <Badge variant="secondary" className="gap-1 text-[11px]"><ClipboardList className="h-3 w-3" />{card.task_title}</Badge>}
                  {card.sop_title && <Badge variant="secondary" className="gap-1 text-[11px]"><BookOpen className="h-3 w-3" />{card.sop_title}</Badge>}
                </div>
              )}
              {card.photo_url && (
                <a href={imgUrl(card.photo_url)} target="_blank" rel="noreferrer">
                  <img src={imgUrl(card.photo_url)} alt="Card evidence" className="max-h-64 w-auto rounded-md border border-slate-200" data-testid="card-detail-photo" />
                </a>
              )}
              <p className="text-[11px] text-slate-400">Cards are a performance signal only — they do not affect salary or payroll.</p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AllCardsDialog({ open, cards, onClose, onOpenCard }) {
  const [page, setPage] = useState(0);
  const per = 9;
  const pages = Math.max(1, Math.ceil(cards.length / per));
  const slice = cards.slice(page * per, (page + 1) * per);
  useEffect(() => {
    if (!open) setPage(0);
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="all-cards-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">All Issued Cards ({cards.length})</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-3" data-testid="all-cards-dialog-list">
          {slice.map((c) => <CardTile key={c.id} c={c} showEmployee onOpen={onOpenCard} />)}
          {slice.length === 0 && <div className="col-span-full"><EmptyState compact icon={Flag} title="No cards issued yet" /></div>}
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="cards-page-prev">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <span className="text-xs text-slate-500">Page {page + 1} of {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid="cards-page-next">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmployeeDetailDialog({ emp, cards, month, onClose, onOpenCard }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    if (!emp) { setEntries([]); return; }
    API.get("/attendance/all", { params: { month, user_id: emp.user_id } }).then((r) => setEntries(r.data)).catch(() => {});
  }, [emp, month]);
  const empCards = cards.filter((c) => c.employee_id === emp?.user_id);
  return (
    <Dialog open={!!emp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="employee-detail-dialog">
        {emp && (
          <>
            <DialogHeader>
              <DialogTitle className="text-base">{emp.employee} — full history ({month})</DialogTitle>
            </DialogHeader>
            <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Cards ({empCards.length})</div>
                {empCards.length === 0 ? (
                  <EmptyState compact icon={Flag} title="No cards on record" testid="employee-detail-no-cards" />
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {empCards.map((c) => <CardTile key={c.id} c={c} onOpen={onOpenCard} />)}
                  </div>
                )}
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Attendance entries</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Clock In</TableHead>
                      <TableHead className="text-right">Clock Out</TableHead>
                      <TableHead className="text-right">Remark</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="employee-detail-attendance">
                    {entries.map((e) => (
                      <TableRow key={e.id} data-testid={`employee-attendance-row-${e.id}`}>
                        <TableCell>{fmtD(e.date)}</TableCell>
                        <TableCell className="text-right">
                          {fmtT(e.clock_in)}
                          {e.geo?.lat != null && (
                            <a href={`https://www.google.com/maps?q=${e.geo.lat},${e.geo.lng}`} target="_blank" rel="noreferrer" title="Clock-in location" data-testid={`geo-link-${e.id}`}>
                              <MapPin className="ml-1 inline h-3.5 w-3.5 text-blue-500 hover:text-blue-700" />
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{fmtT(e.clock_out)}</TableCell>
                        <TableCell className="text-right"><RemarkSpan e={e} testid={`detail-remark-${e.id}`} /></TableCell>
                        <TableCell className="text-right">{e.hours ? `${e.hours}h` : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {entries.length === 0 && (
                      <TableRow><TableCell colSpan={5}><EmptyState compact icon={CalendarDays} title="No attendance entries this month" /></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Performance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canIssue = isAdmin || user?.role === "accountant";
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(isAdmin ? null : []);
  const [prod, setProd] = useState(isAdmin ? null : { products: [], channels: [] });
  const [myCards, setMyCards] = useState([]);
  const [allCards, setAllCards] = useState([]);
  const [lateTasks, setLateTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sops, setSops] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reveal, setReveal] = useState(null);
  const unseenRef = useRef([]);
  const formRef = useRef(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [mySummary, setMySummary] = useState(null);
  const [teamSummary, setTeamSummary] = useState(null);
  const [detailEmp, setDetailEmp] = useState(null);
  const [detailCard, setDetailCard] = useState(null);
  const [allOpen, setAllOpen] = useState(false);

  const loadCards = useCallback(() => {
    API.get("/cards")
      .then((r) => {
        setMyCards(r.data.filter((c) => c.employee_id === user?.id));
        setAllCards(r.data);
      })
      .catch(() => {});
    if (canIssue) {
      API.get("/cards/late-tasks").then((r) => setLateTasks(r.data)).catch(() => {});
      API.get("/users").then((r) => setEmployees(r.data.filter((u) => FIRM2.includes(u.role)))).catch(() => {});
      API.get("/tasks", { params: { filter: "all" } }).then((r) => setTasks(r.data)).catch(() => {});
      API.get("/sops").then((r) => setSops(r.data)).catch(() => {});
    }
  }, [canIssue, user?.id]);

  useEffect(loadCards, [loadCards]);

  useEffect(() => {
    API.get("/attendance/my-summary", { params: { month } }).then((r) => setMySummary(r.data)).catch(() => setMySummary(null));
    if (isAdmin) {
      API.get("/attendance/summary", { params: { month } }).then((r) => setTeamSummary(r.data)).catch(() => {});
    }
  }, [month, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      API.get("/analytics/team-performance").then((r) => setRows(r.data)).catch(() => setRows([]));
      API.get("/analytics/product-performance").then((r) => setProd(r.data)).catch(() => setProd({ products: [], channels: [] }));
    }
  }, [isAdmin]);

  useEffect(() => {
    API.get("/cards/unseen")
      .then((r) => {
        if (r.data.length) {
          unseenRef.current = r.data;
          setReveal({ mode: "receive", card: r.data[0] });
        }
      })
      .catch(() => {});
  }, []);

  const prefillLateTask = useCallback((t) => {
    setForm((f) => ({ ...f, employee_id: t.assigned_to || "", task_id: t.id, reason: f.reason || `Late completion: ${t.title}` }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const lt = searchParams.get("late_task");
    if (!lt || !lateTasks.length) return;
    const t = lateTasks.find((x) => x.id === lt);
    if (t) {
      prefillLateTask(t);
      toast.info("Late task loaded — review the reason and photo before issuing");
    }
    setSearchParams({}, { replace: true });
  }, [lateTasks, searchParams, setSearchParams, prefillLateTask]);

  const submit = async () => {
    if (!form.employee_id) return toast.error("Select an employee");
    if (!form.reason.trim()) return toast.error("Add a reason for the card");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (photo) fd.append("photo", photo);
      const r = await API.post("/cards", fd);
      if (r.data.escalated) toast.warning("3rd yellow in 90 days — auto-escalated to Red");
      setReveal({ mode: "issue", card: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue card");
    } finally {
      setSubmitting(false);
    }
  };

  const onRevealDone = async () => {
    const r = reveal;
    setReveal(null);
    if (r?.mode === "receive") {
      try {
        await API.post(`/cards/${r.card.id}/seen`);
      } catch {}
      unseenRef.current = unseenRef.current.slice(1);
      if (unseenRef.current.length) {
        setReveal({ mode: "receive", card: unseenRef.current[0] });
        return;
      }
      loadCards();
    } else if (r?.mode === "issue") {
      toast.success(`${r.card.color === "red" ? "Red" : r.card.color === "green" ? "Green" : "Yellow"} card issued to ${r.card.employee_name}`);
      setForm(EMPTY_FORM);
      setPhoto(null);
      loadCards();
    }
  };

  if (isAdmin && (!rows || !prod)) return <div className="text-sm text-slate-500" data-testid="performance-loading">Loading performance...</div>;

  const chartData = (rows || [])
    .filter((r) => r.leads_created + r.leads_assigned + r.tasks_completed + r.docs_created > 0)
    .map((r) => ({ name: r.name, won: r.won_value }));
  const heroProducts = (prod.products || []).slice(0, 10);
  const channels = prod.channels || [];
  const topProduct = heroProducts[0];
  const topEmployee = isAdmin && rows.length > 1 ? rows[0] : null;
  const cardCounts = {};
  allCards.forEach((c) => {
    cardCounts[c.employee_id] = cardCounts[c.employee_id] || { yellow: 0, red: 0 };
    cardCounts[c.employee_id][c.color] = (cardCounts[c.employee_id][c.color] || 0) + 1;
  });
  const recentCards = allCards.slice(0, 8);

  const monthPicker = (
    <div className="flex items-center gap-2">
      <CalendarDays className="h-4 w-4 text-slate-400" />
      <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-40" data-testid="performance-month-picker" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="performance-page">
      {reveal && (
        <CardReveal
          color={reveal.card.color}
          title={reveal.mode === "issue" ? `${reveal.card.color === "red" ? "Red" : "Yellow"} card issued` : `You received a ${reveal.card.color} card`}
          sub={reveal.mode === "issue" ? `${reveal.card.employee_name} · ${reveal.card.reason}` : reveal.card.reason}
          onDone={onRevealDone}
        />
      )}

      <CardDetailDialog card={detailCard} onClose={() => setDetailCard(null)} />
      <AllCardsDialog open={allOpen} cards={allCards} onClose={() => setAllOpen(false)} onOpenCard={setDetailCard} />
      <EmployeeDetailDialog emp={detailEmp} cards={allCards} month={month} onClose={() => setDetailEmp(null)} onOpenCard={setDetailCard} />

      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Performance</h1>
        <p className="text-sm text-slate-500">
          {isAdmin ? "Team reviews, cards and analytics" : "Your cards and attendance summary"}
        </p>
      </div>

      {/* 1. Late tasks review + My Cards */}
      <div className={`grid grid-cols-1 gap-6 ${canIssue ? "xl:grid-cols-2" : ""}`}>
        {canIssue && (
          <Card className="border-slate-200" data-testid="late-tasks-panel">
            <CardHeader className={CARD_HEAD}>
              <CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-amber-500" /> Late Task Completions</CardTitle>
            </CardHeader>
            <CardContent className={CARD_BODY}>
              {lateTasks.length === 0 ? (
                <EmptyState compact icon={ClipboardList} title="No late-completed tasks awaiting review" testid="late-tasks-empty" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead className="text-right">Days Late</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-testid="late-tasks-table">
                    {lateTasks.map((t) => (
                      <TableRow key={t.id} data-testid={`late-task-row-${t.id}`}>
                        <TableCell className="font-medium text-slate-900">{t.title}</TableCell>
                        <TableCell className="text-slate-600">{t.assigned_to_name || "—"}</TableCell>
                        <TableCell className="text-right font-semibold text-rose-600">{t.days_late}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => prefillLateTask(t)} data-testid={`late-task-issue-${t.id}`}>Review & Issue</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="mt-3 text-[11px] text-slate-400">A late task never becomes a card automatically — a person must confirm with a reason and photo.</p>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200" data-testid="my-cards-card">
          <CardHeader className={CARD_HEAD}>
            <CardTitle className="flex items-center gap-2 text-base"><Flag className="h-4 w-4 text-amber-500" /> My Cards</CardTitle>
          </CardHeader>
          <CardContent className={CARD_BODY}>
            {myCards.length === 0 ? (
              <EmptyState icon={Flag} title="No cards on your record — keep it up" testid="my-cards-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="my-cards-list">
                {myCards.map((c) => <CardTile key={c.id} c={c} onOpen={setDetailCard} />)}
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-400">Cards are a performance signal only — they do not affect salary or payroll.</p>
          </CardContent>
        </Card>
      </div>

      {/* 2. Team Overview (admin) */}
      {isAdmin && (
        <Card className="border-slate-200" data-testid="team-overview-card">
          <CardHeader className={CARD_HEAD}>
            <CardTitle className="flex items-center justify-between text-base">
              <span>Team Overview — Firm 2</span>
              {monthPicker}
            </CardTitle>
          </CardHeader>
          <CardContent className={CARD_BODY}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Yellow</TableHead>
                  <TableHead className="text-right">Red</TableHead>
                  <TableHead className="text-right">Present</TableHead>
                  <TableHead className="text-right">Absent</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">History</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="team-overview-table">
                {(teamSummary?.rows || []).map((r) => {
                  const cc = cardCounts[r.user_id] || { yellow: 0, red: 0 };
                  return (
                    <TableRow key={r.user_id} data-testid={`team-row-${r.user_id}`}>
                      <TableCell className="font-medium text-slate-900">
                        {r.employee}
                        <span className="ml-1 text-xs capitalize text-slate-400">({r.role})</span>
                      </TableCell>
                      <TableCell className="text-right">{cc.yellow > 0 ? <span className="font-semibold text-amber-600">{cc.yellow}</span> : 0}</TableCell>
                      <TableCell className="text-right">{cc.red > 0 ? <span className="font-semibold text-rose-600">{cc.red}</span> : 0}</TableCell>
                      <TableCell className="text-right text-emerald-700">{r.present}</TableCell>
                      <TableCell className="text-right text-slate-600">{r.absent}</TableCell>
                      <TableCell className="text-right">{r.late}</TableCell>
                      <TableCell className="text-right">{r.hours}h</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDetailEmp(r)} data-testid={`team-view-${r.user_id}`}>View</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <p className="mt-3 text-[11px] text-slate-400">Card counts are all-time; attendance columns follow the selected month. Cards carry no salary impact — payroll lives separately under Payroll.</p>
          </CardContent>
        </Card>
      )}

      {/* 3. My Attendance Summary */}
      <Card className="border-slate-200" data-testid="my-attendance-card">
        <CardHeader className={CARD_HEAD}>
          <CardTitle className="flex items-center justify-between text-base">
            <span>My Attendance Summary — {month}</span>
            {!isAdmin && monthPicker}
          </CardTitle>
        </CardHeader>
        <CardContent className={CARD_BODY}>
          {mySummary ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="my-attendance-summary">
              {[
                ["Working days (so far)", mySummary.elapsed_days],
                ["Present", mySummary.present],
                ["Absent", mySummary.absent],
                ["Late", mySummary.late],
                ["Hours", `${mySummary.hours}h`],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div>
                  <div className="mt-1 text-lg font-bold text-slate-900">{v}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-3 text-sm text-slate-500">Loading attendance summary...</p>
          )}
        </CardContent>
      </Card>

      {/* 4. Issue a Card */}
      {canIssue && (
        <Card className="border-slate-200" data-testid="issue-card-form" ref={formRef}>
          <CardHeader className={CARD_HEAD}>
            <CardTitle className="flex items-center gap-2 text-base"><Flag className="h-4 w-4 text-rose-500" /> Issue a Card (Admin / Accountant)</CardTitle>
          </CardHeader>
          <CardContent className={`${CARD_BODY} space-y-3`}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-56 flex-1">
                <Label>Employee (Firm 2)</Label>
                <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="card-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select employee</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Card color</Label>
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, color: "yellow" })}
                    data-testid="card-color-yellow"
                    className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${form.color === "yellow" ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500 hover:border-amber-300"}`}
                  >
                    <span className="h-4 w-2.5 rounded-[2px] bg-yellow-400" /> Yellow
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, color: "red" })}
                    data-testid="card-color-red"
                    className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${form.color === "red" ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-200 text-slate-500 hover:border-rose-300"}`}
                  >
                    <span className="h-4 w-2.5 rounded-[2px] bg-red-600" /> Red
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, color: "green" })}
                    data-testid="card-color-green"
                    className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${form.color === "green" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500 hover:border-emerald-300"}`}
                  >
                    <span className="h-4 w-2.5 rounded-[2px] bg-emerald-500" /> Green
                  </button>
                </div>
              </div>
              <Button className="bg-rose-600 hover:bg-rose-700" onClick={submit} disabled={submitting} data-testid="card-submit-button">
                {submitting ? "Issuing..." : "Issue Card"}
              </Button>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" rows={2} placeholder="What happened and why this card is being issued" data-testid="card-reason-input" />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Label>Linked task (optional)</Label>
                <Select value={form.task_id || "none"} onValueChange={(v) => setForm({ ...form, task_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="card-task-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked SOP (optional)</Label>
                <Select value={form.sop_id || "none"} onValueChange={(v) => setForm({ ...form, sop_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="card-sop-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Photo evidence (optional)</Label>
                <Input type="file" accept="image/*" className="mt-1.5" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="card-photo-input" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. All Issued Cards */}
      {canIssue && (
        <Card className="border-slate-200" data-testid="all-cards-card">
          <CardHeader className={CARD_HEAD}>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2"><Flag className="h-4 w-4 text-slate-400" /> All Issued Cards ({allCards.length})</span>
              {allCards.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setAllOpen(true)} data-testid="cards-view-all">View all</Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className={CARD_BODY}>
            {recentCards.length === 0 ? (
              <EmptyState compact icon={Flag} title="No cards issued yet" testid="all-cards-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="all-cards-list">
                {recentCards.map((c) => <CardTile key={c.id} c={c} showEmployee onOpen={setDetailCard} />)}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <>
          {/* 6. Hero cards */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card className="group border-amber-200 bg-amber-50/60 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="hero-product-card">
              <CardContent className="flex items-center gap-4 p-5">
                <GlossyBadge icon={Package} size={44} tone="blue" testid="hero-product-badge" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-600">Hero Product</div>
                  <div className="font-heading text-xl font-bold text-slate-900" data-testid="hero-product-name">{topProduct ? topProduct.name : "—"}</div>
                  {topProduct && <div className="text-xs text-slate-500">{topProduct.qty} sold · {inr(topProduct.revenue)} revenue</div>}
                </div>
              </CardContent>
            </Card>
            <Card className="group border-indigo-200 bg-indigo-50/60 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg" data-testid="hero-employee-card">
              <CardContent className="flex items-center gap-4 p-5">
                <GlossyBadge icon={Trophy} size={44} tone="indigo" testid="hero-employee-badge" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Hero Employee</div>
                  <div className="font-heading text-xl font-bold text-slate-900" data-testid="hero-employee-name">{topEmployee ? topEmployee.name : "—"}</div>
                  {topEmployee && <div className="text-xs text-slate-500">{inr(topEmployee.won_value)} won · {topEmployee.leads_won} deals closed</div>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 7. Products | Channels */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Card className="border-slate-200">
              <CardHeader className={CARD_HEAD}>
                <CardTitle className="flex items-center gap-2 text-base"><ShoppingBag className="h-4 w-4 text-indigo-500" /> Top Products by Revenue (B2C + B2B)</CardTitle>
              </CardHeader>
              <CardContent className={CARD_BODY}>
                <div className="h-64" data-testid="product-performance-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={heroProducts.slice(0, 7)} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => inr(v)} />
                      <Bar dataKey="revenue" fill="#4F46E5" radius={[0, 6, 6, 0]} name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto" data-testid="product-rows-scroll">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Qty Sold</TableHead>
                        <TableHead className="text-right">B2C (Amazon)</TableHead>
                        <TableHead className="text-right">B2B</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="product-performance-table">
                      {heroProducts.map((p, i) => (
                        <TableRow key={p.name}>
                          <TableCell className="font-medium">
                            <span className="mr-1.5 inline-flex items-center">{i === 0 && <Trophy className="mr-1 h-3.5 w-3.5 text-amber-500" />}{p.name}</span>
                          </TableCell>
                          <TableCell className="text-right">{p.qty}</TableCell>
                          <TableCell className="text-right text-slate-500">{p.b2c_qty}</TableCell>
                          <TableCell className="text-right text-slate-500">{p.b2b_qty}</TableCell>
                          <TableCell className="text-right font-semibold text-indigo-700">{inr(p.revenue)}</TableCell>
                        </TableRow>
                      ))}
                      {heroProducts.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState compact icon={ShoppingBag} title="No sales recorded yet" /></TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className={CARD_HEAD}>
                <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-emerald-500" /> Sales by Channel</CardTitle>
              </CardHeader>
              <CardContent className={CARD_BODY}>
                <div className="h-64" data-testid="channel-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={channels} dataKey="total" nameKey="channel" innerRadius={55} outerRadius={95} paddingAngle={3}>
                        {channels.map((c, i) => <Cell key={c.channel} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => inr(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-2" data-testid="channel-list">
                  {channels.map((c, i) => (
                    <div key={c.channel} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                        {c.channel}
                      </span>
                      <span className="font-semibold">{inr(c.total)}</span>
                    </div>
                  ))}
                  {channels.length === 0 && <EmptyState compact icon={TrendingUp} title="No channel sales yet" />}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 8. Won Deal Value */}
          <Card className="border-slate-200">
            <CardHeader className={CARD_HEAD}>
              <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-indigo-500" /> Won Deal Value by Employee</CardTitle>
            </CardHeader>
            <CardContent className={CARD_BODY}>
              <div className="h-44" data-testid="performance-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Bar dataKey="won" fill="#4F46E5" radius={[6, 6, 0, 0]} name="Won value" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* 9. Detailed employee stats */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-right">Leads Added</TableHead>
                  <TableHead className="text-right">Leads Assigned</TableHead>
                  <TableHead className="text-right">Won</TableHead>
                  <TableHead className="text-right">Won Value</TableHead>
                  <TableHead className="text-right">Tasks Done</TableHead>
                  <TableHead className="text-right">Tasks Open</TableHead>
                  <TableHead className="text-right">Quotes/Invoices</TableHead>
                  <TableHead className="text-right">Docs Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="performance-table">
                {rows.map((r, i) => (
                  <TableRow key={r.id} data-testid={`perf-row-${r.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {i === 0 && rows.length > 1 && <Trophy className="h-4 w-4 text-amber-500" data-testid="top-performer-icon" />}
                        <span className="font-medium">{r.name}</span>
                        <Badge className={ROLE_BADGE[r.role] || ROLE_BADGE.staff}>{ROLES[r.role] || r.role}</Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{r.leads_created}</TableCell>
                    <TableCell className="text-right">{r.leads_assigned}</TableCell>
                    <TableCell className="text-right">{r.leads_won}</TableCell>
                    <TableCell className="text-right font-semibold text-indigo-700">{inr(r.won_value)}</TableCell>
                    <TableCell className="text-right">{r.tasks_completed}</TableCell>
                    <TableCell className="text-right">{r.tasks_open}</TableCell>
                    <TableCell className="text-right">{r.docs_created}</TableCell>
                    <TableCell className="text-right">{inr(r.docs_value)}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9}><EmptyState compact title="No team members yet" /></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
